import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { searchPlaceholder } from "@revbox/localization";
import { getCachedEurUsdRateSync } from "@revbox/helpers-fe"
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-charts-enterprise";

import "../../../../../../scss/components/_ag-table.scss";

import { useCookiesContext } from "../../../../../../contexts/CookiesContext";
import { useRateTooltip } from "../../../../../../hooks/useRateTooltip";
import { useJoyride } from "../../../../../../contexts/JoyrideContext";
import { useDebouncedCallback } from "../../../../../../hooks/useDebounceCallback";
import { CampaignTableFilters } from "../../../../../../data/table-filters";
import { defaultColDef, fetchClusterAuctions, filterAuctions, getAutoGroupColumnDef, getRowClass, setIdToTourElement } from "./utils";

export const ClusterPPCAuctions = memo(({
  getData,
  skin,
  onClick,
  campaign_is_paused,
  setClusterActiveAuctions,
  setClusterInheritedAuctionsAndNotShown,
  setNumberOfClusterAuctions,
  updatePage,
}) => {
  const urlParams = new URLSearchParams(window.location.search)
  const campaign_id = urlParams.get('campaignId')

  const { isInitialTourRunning } = useJoyride();
  const [expandedLevel, setExpandedLevel] = useState(1);

  useEffect(() => {
    if (isInitialTourRunning()) {
      setExpandedLevel(-1)
    }
  }, [isInitialTourRunning])

  const [data, setData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(0);

  const [isLoading, setIsLoading] = useState(false);

  const { getLanguageAbbr } = useCookiesContext();
  const languageAbbr = getLanguageAbbr();
  
  const gridRef = useRef(null);
  
  const [autoGroupColumnDef, setAutoGroupColumnDef] = useState(
    getAutoGroupColumnDef(languageAbbr)
  );

  useEffect(() => {
    setAutoGroupColumnDef(getAutoGroupColumnDef(languageAbbr));
  }, [languageAbbr]);

  const rate = getCachedEurUsdRateSync();
  useRateTooltip(rate);
  
  useEffect(() => {
    setIsLoading(true);
    fetchClusterAuctions(getData, setColumnDefs, search, campaign_id,languageAbbr, statusFilter)
      .then((data) => {
        setNumberOfClusterAuctions(data.rows ? data.rows.length : data.length);
  
        const { activeAuctions, inheritedAndNotShown } = filterAuctions(
          data.rows ?? data,
          search,
          languageAbbr
        );
  
        if (!search && !isInitialTourRunning()) {
          setClusterActiveAuctions(activeAuctions);
          setExpandedLevel(1);
        } else {
          setExpandedLevel(-1);
        }
  
        setClusterInheritedAuctionsAndNotShown(inheritedAndNotShown);
        setData(data.rows ?? data);
      })
      .catch(console.error)
      .finally(() => {
        setIsLoading(false);
      });
  
    // updatePage is used to refresh the tabs when the page is updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, campaign_id, statusFilter, updatePage, languageAbbr]);

  const getDataPath = useCallback((data) => data.path, []);

  const onCellKeyDown = useCallback((params) => {
    if (!("colDef" in params)) {
      return;
    }
    if (!(params.event instanceof KeyboardEvent)) {
      return;
    }
    if (params.event.code !== "Enter") {
      return;
    }
    if (params.colDef.showRowGroup) {
      params.node.setExpanded(!params.node.expanded);
    }
  }, []);

  const onCellDoubleClicked = useCallback((params) => {
    if (params.colDef.showRowGroup) {
      params.node.setExpanded(!params.node.expanded);
    }
  }, []);

  const [query, setQuery] = useState('');

  const handleInputChange = (e) => {
    const v = e.target.value;
    setQuery(v);           // immediately updates the UI
    applyDebounced(v);     // delayed search // this is not a good approach, but it's work
  };

  const applyDebounced = useDebouncedCallback((value) => {
    setStatusFilter(0);
    setSearch(value);      // this affects the query/filter
    setIsLoading(true);
  }, 500);
  
  const handleStatusFilter = (status) => {
    setSearch("");
    setQuery("");
    setStatusFilter(status)
    setIsLoading(true)
  }

  setIdToTourElement();
  const skinMode = skin === "dark" ? "dark" : "light";
  const themeClass = `ag-theme-quartz-${skinMode} ag-theme-quartz`;

  return (
    <Card className="card-one cluster-ppc-auctions">
      <Card.Body>
        <Row>
          <Col md="10" className={themeClass}>
            <input
              type="text"
              className="input-search"
              placeholder={searchPlaceholder[languageAbbr]}
              onChange={handleInputChange}
              value={query}
              style={{ marginBottom: "10px" }}
            />
          </Col>
          <Col md="2">
            <CampaignTableFilters 
              status={statusFilter} 
              setStatus={handleStatusFilter} 
              ppc_paused={campaign_is_paused} 
              isClusterTable={true} 
            />
          </Col>
        </Row>
        <div style={{ width: "100%", height: "620px" }}>
          <div
            style={{ width: "100%", height: "100%" }}
            className={themeClass}
          >
            <AgGridReact
              ref={gridRef}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              autoGroupColumnDef={autoGroupColumnDef}
              getRowClass={getRowClass}
              rowData={data}
              rowHeight={65}
              suppressContextMenu={true}
              onCellClicked={onClick}
              loading={isLoading}
              treeData={true}
              groupDefaultExpanded={expandedLevel}
              getDataPath={getDataPath}
              onCellDoubleClicked={onCellDoubleClicked}
              onCellKeyDown={onCellKeyDown}
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
});
